import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdApps, MdSearch, MdTune } from 'react-icons/md';

import {
  disconnectConnectedApp,
  fetchAutoApprovals,
  fetchConnectedApps,
  removeAutoApproval,
  requestConnectedApp,
  startConnectedAppConnect,
} from './atlas/atlasApi';

// Settings → Connectors: the user's own apps (Google, HubSpot, CRMs, accounting,
// project tools...) connected through Composio, grouped by category with a search
// box. Apps that aren't available yet stay hidden; the request form below
// covers them. Renders nothing while the server has the feature off.

const POLL_MS = 3000;

// Short labels for the filter buttons; the full category name heads each group.
const CATEGORY_SHORT = {
  crm: 'CRM',
  files: 'Files',
  communication: 'Communication',
  accounting: 'Accounting',
  field: 'Field Ops',
  projects: 'Projects',
  marketing: 'Marketing',
  esign: 'E-sign',
};
const POLL_LIMIT_MS = 3 * 60 * 1000;

function statusLine(app, ruleCount = 0) {
  if (app.connected && app.read_only) return 'Connected · read-only';
  if (app.connected && ruleCount) return `Connected — ${ruleCount} ${ruleCount === 1 ? 'action runs' : 'actions run'} without asking`;
  if (app.connected) return 'Connected — changes need your approval';
  if (app.connect === 'api_key') return 'Connects with your API key';
  return app.read_only ? 'Read-only' : '';
}

// The actions in one app that the user chose "Always allow" for on an approval
// card. Everything else in the app still waits for a click.
function PermissionsPanel({ app, rules, busy, onRemoveRule }) {
  return (
    <div className="settings-connector-permissions" id={`permissions-${app.toolkit}`}>
      {rules.length === 0 ? (
        <p>
          Atlas asks before every change in {app.name}. Choose “Always allow” on an approval card to let one
          action run without asking.
        </p>
      ) : (
        <>
          <p>These run without an approval card. Everything else in {app.name} still asks first.</p>
          <ul>
            {rules.map((rule) => (
              <li key={rule.tool_slug}>
                <span>{rule.action}</span>
                <button
                  type="button"
                  className="settings-connector-app-button"
                  onClick={() => onRemoveRule(rule.tool_slug)}
                  disabled={busy === rule.tool_slug}
                  aria-label={`Stop always allowing ${rule.action}`}
                >
                  {busy === rule.tool_slug ? 'Removing…' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function AppRow({ app, busy, rules = [], permissionsOpen, onTogglePermissions, onRemoveRule, onConnect, onDisconnect }) {
  const working = busy === app.toolkit;
  // Read-only apps never stage changes; a leftover rule on a disconnected app
  // still gets the button so it can be removed.
  const hasPermissions = (app.connected && !app.read_only) || rules.length > 0;
  return (
    <div className="settings-connector-row settings-connector-app">
      <span className="settings-connector-icon">
        {app.logo ? <img src={app.logo} alt="" width={20} height={20} /> : <MdApps size={19} />}
      </span>
      <span className="settings-connector-row-copy">
        <strong>{app.name}</strong>
        {statusLine(app, rules.length) && <small title={app.note || undefined}>{statusLine(app, rules.length)}</small>}
      </span>
      <span className="settings-connector-app-actions">
        {hasPermissions && (
          <button
            type="button"
            className={`settings-connector-app-button settings-connector-permissions-toggle${permissionsOpen ? ' open' : ''}`}
            onClick={() => onTogglePermissions(app.toolkit)}
            aria-expanded={permissionsOpen}
            aria-controls={`permissions-${app.toolkit}`}
            aria-label={`Permissions for ${app.name}`}
          >
            <MdTune size={15} />
            <span className="settings-connector-permissions-label">Permissions</span>
            {rules.length > 0 && <span className="settings-connector-permissions-count">{rules.length}</span>}
          </button>
        )}
        {app.connected ? (
          <button type="button" className="settings-connector-app-button" onClick={() => onDisconnect(app.toolkit)} disabled={working}>
            {working ? 'Working…' : 'Disconnect'}
          </button>
        ) : (
          <button type="button" className="settings-connector-app-button primary" onClick={() => onConnect(app.toolkit)} disabled={working}>
            {working ? 'Opening…' : 'Connect'}
          </button>
        )}
      </span>
      {hasPermissions && permissionsOpen && (
        <PermissionsPanel app={app} rules={rules} busy={busy} onRemoveRule={onRemoveRule} />
      )}
    </div>
  );
}

export default function ConnectedAppsList() {
  const [apps, setApps] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestName, setRequestName] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [requestDone, setRequestDone] = useState('');
  const [autoRules, setAutoRules] = useState([]);
  const [openPermissions, setOpenPermissions] = useState('');
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchConnectedApps();
      setEnabled(Boolean(data?.enabled));
      const all = Array.isArray(data?.apps) ? data.apps : [];
      setApps(all.filter((a) => a.available || a.connected));
      if (data?.enabled) {
        fetchAutoApprovals()
          .then((rules) => setAutoRules(Array.isArray(rules?.rules) ? rules.rules : []))
          .catch(() => {});
      }
    } catch (e) {
      if (e?.status !== 404) setError('Could not load your connected apps.');
    }
  }, []);

  const onRemoveRule = useCallback(async (toolSlug) => {
    setError('');
    setBusy(toolSlug);
    try {
      await removeAutoApproval(toolSlug);
      setAutoRules((prev) => prev.filter((r) => r.tool_slug !== toolSlug));
    } catch (e) {
      setError(e?.message || 'Could not remove that rule.');
    } finally {
      setBusy('');
    }
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [refresh]);

  const watchSignIn = useCallback((popup) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const started = Date.now();
    pollRef.current = setInterval(() => {
      refresh();
      if ((popup && popup.closed) || Date.now() - started > POLL_LIMIT_MS) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        refresh();
      }
    }, POLL_MS);
  }, [refresh]);

  const onConnect = useCallback(async (toolkit) => {
    setError('');
    setBusy(toolkit);
    // Open the tab inside the click so popup blockers allow it, then point it at
    // the provider's sign-in once the server returns the link.
    const popup = window.open('', '_blank');
    try {
      const { redirect_url: url } = await startConnectedAppConnect(toolkit, `${window.location.origin}/connected-app.html`);
      if (popup) {
        popup.location.href = url;
        watchSignIn(popup);
      } else {
        window.location.href = url;
      }
    } catch (e) {
      if (popup) popup.close();
      setError(e?.message || 'Could not start the sign-in.');
    } finally {
      setBusy('');
    }
  }, [watchSignIn]);

  const onDisconnect = useCallback(async (toolkit) => {
    setError('');
    setBusy(toolkit);
    try {
      await disconnectConnectedApp(toolkit);
      await refresh();
    } catch (e) {
      setError(e?.message || 'Could not disconnect.');
    } finally {
      setBusy('');
    }
  }, [refresh]);

  const submitRequest = useCallback(async (appName, note) => {
    const name = String(appName || '').trim();
    if (!name) return;
    setError('');
    setBusy(name);
    try {
      await requestConnectedApp(name, note);
      setRequestDone(name);
      setRequestOpen(false);
      setRequestName('');
      setRequestNote('');
    } catch (e) {
      setError(e?.message || 'Could not send the request.');
    } finally {
      setBusy('');
    }
  }, []);

  const openRequest = useCallback((name) => {
    setRequestName(name || '');
    setRequestDone('');
    setRequestOpen(true);
  }, []);

  const { connected, groups } = useMemo(() => {
    const q = query.trim().toLowerCase();
    const inCategory = (a) => category === 'all' || a.category === category;
    const matches = (a) => inCategory(a) && (!q || a.name.toLowerCase().includes(q) || (a.category_label || '').toLowerCase().includes(q));
    const visible = apps.filter(matches);
    const byCategory = [];
    const index = {};
    for (const app of visible) {
      // In the All view connected apps are pulled up into their own group.
      if (app.connected && !q && category === 'all') continue;
      const key = app.category_label || 'Other';
      if (!(key in index)) {
        index[key] = byCategory.length;
        byCategory.push({ label: key, apps: [] });
      }
      byCategory[index[key]].apps.push(app);
    }
    const showConnected = !q && category === 'all';
    return { connected: showConnected ? visible.filter((a) => a.connected) : [], groups: byCategory };
  }, [apps, query, category]);

  const categories = useMemo(() => {
    const seen = [];
    for (const app of apps) {
      if (app.category && !seen.some((c) => c.key === app.category)) {
        seen.push({ key: app.category, label: CATEGORY_SHORT[app.category] || app.category_label || app.category });
      }
    }
    return seen;
  }, [apps]);

  if (!enabled) return null;

  const rowProps = {
    busy,
    onConnect,
    onDisconnect,
    onRemoveRule,
    onTogglePermissions: (toolkit) => setOpenPermissions((open) => (open === toolkit ? '' : toolkit)),
  };
  const appRow = (app) => (
    <AppRow
      key={app.toolkit}
      app={app}
      rules={autoRules.filter((r) => String(r.toolkit || '').toLowerCase() === app.toolkit)}
      permissionsOpen={openPermissions === app.toolkit}
      {...rowProps}
    />
  );
  return (
    <div className="settings-connected-apps" data-testid="connected-apps">
      <div className="settings-connected-apps-head">
        <h3 className="settings-connected-apps-title">Apps</h3>
        <span className="settings-connected-apps-beta">Beta</span>
      </div>
      <label className="settings-connected-apps-search">
        <MdSearch size={17} />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search connectors"
          aria-label="Search connectors"
        />
      </label>
      <div className="settings-connected-apps-filters" role="group" aria-label="Filter by category">
        {[{ key: 'all', label: 'All' }, ...categories].map((c) => (
          <button
            type="button"
            key={c.key}
            className={`settings-connected-apps-filter${category === c.key ? ' active' : ''}`}
            aria-pressed={category === c.key}
            onClick={() => setCategory(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {connected.length > 0 && (
        <div className="settings-connected-apps-group">
          <h4>Connected</h4>
          {connected.map(appRow)}
        </div>
      )}

      {groups.map((group) => (
        <div className="settings-connected-apps-group" key={group.label}>
          <h4>{group.label}</h4>
          {group.apps.map(appRow)}
        </div>
      ))}

      {!query && category !== 'all' && groups.length === 0 && (
        <div className="settings-connected-apps-empty">No apps in this category yet.</div>
      )}

      {query && groups.length === 0 && (
        <div className="settings-connected-apps-empty">
          No connectors match “{query}”.{' '}
          <button type="button" className="settings-connected-apps-link" onClick={() => openRequest(query.trim())}>
            Request “{query.trim()}”
          </button>
        </div>
      )}

      {requestDone && !requestOpen && (
        <div className="settings-connected-apps-done">
          Thanks — we got your request for {requestDone}. We'll let you know when it's available.
        </div>
      )}

      {requestOpen ? (
        <form
          className="settings-connected-apps-request"
          onSubmit={(e) => {
            e.preventDefault();
            submitRequest(requestName, requestNote.trim());
          }}
        >
          <strong>Request an app</strong>
          <input
            type="text"
            value={requestName}
            onChange={(e) => setRequestName(e.target.value)}
            placeholder="App name, e.g. JobNimbus"
            aria-label="App name"
            maxLength={120}
            required
          />
          <textarea
            value={requestNote}
            onChange={(e) => setRequestNote(e.target.value)}
            placeholder="What would you use it for? (optional)"
            aria-label="What would you use it for"
            maxLength={1000}
            rows={3}
          />
          <div className="settings-connected-apps-request-actions">
            <button type="submit" className="settings-connector-app-button primary" disabled={!requestName.trim() || Boolean(busy)}>
              {busy ? 'Sending…' : 'Send request'}
            </button>
            <button type="button" className="settings-connector-app-button" onClick={() => setRequestOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" className="settings-connected-apps-link" onClick={() => openRequest(query.trim())}>
          Don't see your app? Request it
        </button>
      )}
      {error && <div className="settings-connected-apps-error">{error}</div>}
    </div>
  );
}
